import React, { useState, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Stack, Chip, Alert, Divider, TextField,
  InputAdornment, Select, MenuItem, FormControl,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Radio, Tooltip, Autocomplete, IconButton,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import PercentIcon from '@mui/icons-material/Percent';
import TuneIcon from '@mui/icons-material/Tune';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useData } from '../context/DataContext';
import { PRICE_BOOKS } from '../data/pricebooks';
import { BRAND } from '../theme/tokens';
import { classifyImportLine, mintInternalCode } from '../utils/codeMatching';
import type { SupplierPriceBookEntry } from '../data/types';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

const FI_ORANGE = '#F57C00';
const FI_BLUE   = '#027BFF';

// v1.14.1 — Pricing strategy chosen at CSV import time. Drives whether the
// sales price for each row comes from cost × (1 + markup), a one-off custom
// markup, or the supplier's recommended retail price (RRP) column.
type PricingStrategy = 'default-margin' | 'custom-margin' | 'supplier-rrp';

// How a parsed supplier row resolves against the supplier-agnostic internal
// catalogue. The supplier code NEVER becomes the internal code — it is linked
// to an existing internal item or a freshly minted company code (FIE-234).
type RowResolution = 'matched' | 'suggested' | 'new';
type RowAction = 'link' | 'create' | 'skip';

interface ImportedRow {
  code: string;
  description: string;
  price: number;          // supplier cost (Unit Price column)
  rrp?: number;           // supplier RRP if present in CSV
  salesPrice: number;     // final sales price computed per chosen strategy
  valid: boolean;
  rrpMissing?: boolean;   // RRP strategy chosen but this row had no RRP
  // Resolution against the internal catalogue
  resolution: RowResolution;
  matchMethod?: string;
  action: RowAction;
  linkCode?: string;      // internal code to link to when action === 'link'
}

export const SupplierPriceBooksPage: React.FC = () => {
  const {
    suppliers, products, supplierPriceBook,
    addProduct, addSupplierPriceBookEntries, addSupplierCodeMap,
    lookupSupplierCode, lookupSupplierPriceBookEntry, internalCodePrefix,
  } = useData();

  const firstSupplierId = suppliers[0]?.id ?? '';
  const [selectedSupplier, setSelectedSupplier] = useState(firstSupplierId);
  const [importRows, setImportRows] = useState<ImportedRow[]>([]);
  const [importDone, setImportDone] = useState(false);
  const [search, setSearch] = useState('');
  const [supplierMarkups, setSupplierMarkups] = useState<Record<string, number>>(
    Object.fromEntries(suppliers.map(s => [s.id, s.defaultMarkup]))
  );
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Import pricing-strategy chooser (v1.14.1) ─────────────────────────────
  // Old flow: click "Import" → file picker → blindly apply supplier default
  // markup to every row. Now we open a strategy dialog first so the user can:
  //   1. Apply the supplier's default margin
  //   2. Apply a one-off custom margin (for this import only)
  //   3. Use the supplier's RRP column directly (no markup applied)
  // The chosen strategy drives the salesPrice column shown in the preview.
  const [strategyOpen, setStrategyOpen] = useState(false);
  const [strategy, setStrategy] = useState<PricingStrategy>('default-margin');
  const [customMargin, setCustomMargin] = useState<number>(0);

  const supId = selectedSupplier || firstSupplierId;
  const supplier = suppliers.find(s => s.id === supId) ?? suppliers[0];
  const priceBook = PRICE_BOOKS[supId] ?? {};
  const defaultMarkup = supplierMarkups[supId] ?? supplier?.defaultMarkup ?? 0;
  const setDefaultMarkup = (v: number) => setSupplierMarkups(prev => ({ ...prev, [supId]: v }));

  // v1.18 — supplier codes created via quote/bill import, persisted per supplier.
  const supplierCodes = supplierPriceBook
    .filter(e => e.supplierId === supId)
    .filter(e =>
      !search ||
      e.supplierCode.toLowerCase().includes(search.toLowerCase()) ||
      e.description.toLowerCase().includes(search.toLowerCase())
    );

  const matchedProducts = products.filter(p => priceBook[p.code] !== undefined);
  const filtered = matchedProducts.filter(p =>
    p.description.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase())
  );

  const priceValues = matchedProducts.map(p => priceBook[p.code] ?? 0);
  const minPrice = priceValues.length > 0 ? Math.min(...priceValues) : 0;
  const maxPrice = priceValues.length > 0 ? Math.max(...priceValues) : 0;

  // Two-step import: open the strategy dialog first, then chain into the
  // file picker once the user confirms the pricing approach.
  const openImportFlow = () => {
    // Seed custom-margin with the supplier's default for one-click adjust
    setCustomMargin(defaultMarkup);
    setStrategy('default-margin');
    setStrategyOpen(true);
  };

  const proceedFromStrategy = () => {
    setStrategyOpen(false);
    // Defer the click so the dialog can finish unmounting (some browsers
    // block file pickers that fire inside the same task as a Dialog close).
    setTimeout(() => fileRef.current?.click(), 60);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const rawLines = text.split('\n').filter(l => l.trim());
      if (rawLines.length === 0) return;
      // Detect the optional RRP column from the header so older templates
      // (Item Code,Description,Unit Price) still work unchanged.
      const headerCols = rawLines[0].split(',').map(s => s.trim().toLowerCase().replace(/"/g, ''));
      const rrpIdx = headerCols.findIndex(h => h === 'rrp' || h === 'recommended retail price' || h === 'retail price');
      const dataLines = rawLines.slice(1);
      const parsed: ImportedRow[] = dataLines.map(line => {
        const cols = line.split(',').map(s => s.trim().replace(/"/g, ''));
        const [code, description, priceStr] = cols;
        const price = parseFloat(priceStr) || 0;
        const rrpStr = rrpIdx >= 0 ? cols[rrpIdx] : undefined;
        const rrp = rrpStr !== undefined ? parseFloat(rrpStr) : undefined;
        const rrpMissing = strategy === 'supplier-rrp' && (rrp === undefined || isNaN(rrp) || rrp <= 0);
        let salesPrice = 0;
        if (strategy === 'supplier-rrp' && rrp !== undefined && rrp > 0) {
          salesPrice = rrp;
        } else {
          // Either default-margin / custom-margin, OR RRP strategy with a
          // missing RRP — gracefully fall back to default markup so the
          // row still imports with a sensible sales price.
          const mk = strategy === 'custom-margin' ? customMargin : defaultMarkup;
          salesPrice = price * (1 + mk / 100);
        }

        // Resolve the supplier code against the internal catalogue using the
        // (supplier-agnostic) match ladder: remembered map -> linked price-book
        // entry -> exact/suffix/fuzzy. No prefix coupling.
        const state = classifyImportLine({
          supplierCode: code,
          description: description || code,
          supplierName: supplier?.name ?? '',
          products,
          priceBookEntry: lookupSupplierPriceBookEntry(supId, code) ?? null,
          remembered: lookupSupplierCode(supId, code),
        });
        const resolution: RowResolution =
          state.status === 'matched' ? 'matched'
            : state.status === 'suggested' ? 'suggested'
              : 'new';
        const linkCode = state.internalCode || state.suggestedInternal || undefined;

        return {
          code,
          description: description || code,
          price,
          rrp: rrp && !isNaN(rrp) ? rrp : undefined,
          salesPrice,
          rrpMissing,
          valid: !!code && price > 0,
          resolution,
          matchMethod: state.matchMethod,
          action: (resolution === 'new' ? 'create' : 'link') as RowAction,
          linkCode,
        };
      });
      setImportRows(parsed);
      setImportDone(false);
    };
    reader.readAsText(file);
    // Reset the file input so re-selecting the same file fires onChange again
    e.target.value = '';
  };

  // ── Per-row + bulk resolution controls ──────────────────────────────────────
  const setRowAction = (idx: number, action: RowAction) =>
    setImportRows(prev => prev.map((r, i) => (i === idx ? { ...r, action } : r)));
  const setRowLink = (idx: number, linkCode: string | undefined) =>
    setImportRows(prev => prev.map((r, i) =>
      i === idx ? { ...r, linkCode, action: linkCode ? 'link' : 'create' } : r));
  const bulkConfirmSuggested = () =>
    setImportRows(prev => prev.map(r =>
      r.resolution === 'suggested' && r.action !== 'skip' ? { ...r, action: 'link' } : r));
  const bulkCreateNew = () =>
    setImportRows(prev => prev.map(r =>
      r.resolution === 'new' && r.action !== 'skip' ? { ...r, action: 'create', linkCode: undefined } : r));

  // Real persistence: link to existing internal items or MINT new neutral codes,
  // then write the cross-reference (price-book info record) + the learned
  // supplier-code -> internal-code map so future imports auto-resolve.
  const handleConfirmImport = () => {
    let workingProducts = [...products];
    let idCounter = Date.now();
    const entries: Array<{
      supplierId: string; supplierCode: string; description: string;
      unitPrice: number; internalCode?: string; stocked: boolean; source: 'csv';
    }> = [];
    const maps: Array<{ supplierCode: string; internalCode: string }> = [];

    for (const r of importRows) {
      if (!r.valid || r.action === 'skip') continue;
      let internalCode = r.action === 'link' ? r.linkCode : undefined;
      if (!internalCode) {
        // Mint a supplier-agnostic code and create the internal item.
        internalCode = mintInternalCode(workingProducts, internalCodePrefix);
        const created = addProduct({
          id: idCounter++,
          code: internalCode,
          description: r.description,
          category: 'Uncategorised',
          primarySupplier: supId,
          purchasePrice: r.price,
          markup: defaultMarkup,
          stock: 0,
          minStock: 0,
        });
        workingProducts = [created, ...workingProducts];
      }
      entries.push({
        supplierId: supId,
        supplierCode: r.code,
        description: r.description,
        unitPrice: r.price,
        internalCode,
        stocked: true,
        source: 'csv',
      });
      maps.push({ supplierCode: r.code, internalCode });
    }

    if (entries.length) addSupplierPriceBookEntries(entries);
    maps.forEach(m => addSupplierCodeMap({ supplierId: supId, supplierCode: m.supplierCode, internalCode: m.internalCode }));
    setImportDone(true);
    setImportRows([]);
  };

  const downloadTemplate = () => {
    // v1.14.1 — template now includes an optional RRP column so the
    // supplier-RRP import strategy works end-to-end out of the box.
    const csv =
      'Item Code,Description,Unit Price,RRP\n' +
      'HC-WDB-06/16,Clamp Wormdrive 6-16mm,1.29,1.99\n' +
      '06FH,Fuel Hose 10mm per metre,9.17,13.95\n' +
      'BR-HTHT-0608,3/8 x 1/2 Hose Barb,6.87,9.45\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'price-book-template.csv'; a.click();
  };

  // Strategy-card definition lives near the dialog. Pulled out so the JSX
  // stays declarative and easy to extend (e.g. "tiered markup" later).
  const strategyOptions: Array<{
    id: PricingStrategy;
    title: string;
    summary: string;
    icon: React.ReactNode;
  }> = [
    {
      id: 'default-margin',
      title: `Apply ${supplier?.name ?? 'supplier'} default margin (${defaultMarkup}%)`,
      summary: 'Sales price = supplier cost × (1 + default markup). Best for routine refreshes from this supplier.',
      icon: <PercentIcon sx={{ fontSize: 22, color: BRAND }} />,
    },
    {
      id: 'custom-margin',
      title: 'Apply a custom margin for this import',
      summary: 'One-off markup. Useful for promotional pricing or supplier-specific deals that differ from the standard rate.',
      icon: <TuneIcon sx={{ fontSize: 22, color: FI_BLUE }} />,
    },
    {
      id: 'supplier-rrp',
      title: 'Use supplier RRP (no markup)',
      summary: 'Pulls the RRP column from the CSV directly as the sales price. Falls back to the default margin if RRP is missing on a row.',
      icon: <LocalOfferIcon sx={{ fontSize: 22, color: FI_ORANGE }} />,
    },
  ];

  const strategyChip = strategy === 'default-margin'
    ? `Default ${defaultMarkup}%`
    : strategy === 'custom-margin'
      ? `Custom ${customMargin}%`
      : 'Supplier RRP';

  // ── Cross-supplier comparison (the multi-sourcing payoff) ───────────────────
  // Group every linked price-book entry by its supplier-agnostic internal item.
  // Items supplied by two or more suppliers surface here so a buyer can see who
  // is cheapest for the same physical item.
  const comparisonGroups = (() => {
    const byItem = new Map<string, SupplierPriceBookEntry[]>();
    for (const e of supplierPriceBook) {
      const code = (e.internalCode || '').trim();
      if (!code) continue;
      const arr = byItem.get(code) ?? [];
      arr.push(e);
      byItem.set(code, arr);
    }
    return Array.from(byItem.entries())
      .map(([code, entries]) => {
        // Collapse to one entry per supplier (keep the cheapest if duplicated).
        const perSupplier = new Map<string, SupplierPriceBookEntry>();
        for (const e of entries) {
          const cur = perSupplier.get(e.supplierId);
          if (!cur || e.unitPrice < cur.unitPrice) perSupplier.set(e.supplierId, e);
        }
        const rows = Array.from(perSupplier.values()).sort((a, b) => a.unitPrice - b.unitPrice);
        const product = products.find(p => p.code === code);
        return { code, product, rows };
      })
      .filter(g => g.rows.length >= 2)
      .sort((a, b) => b.rows.length - a.rows.length);
  })();

  if (!supplier) {
    return <Box><Typography>No suppliers found. Reset data to reload.</Typography></Box>;
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: BRAND }}>Supplier Price Books</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage per-supplier pricing for inventory items. Import a CSV price list from any supplier.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />}
            onClick={downloadTemplate} sx={{ textTransform: 'none' }}>
            Download template
          </Button>
          <Button variant="contained" size="small" startIcon={<FileUploadIcon />}
            onClick={openImportFlow}
            sx={{ backgroundColor: FI_ORANGE, textTransform: 'none', '&:hover': { backgroundColor: '#e65100' } }}>
            Import price book CSV
          </Button>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileUpload} />
        </Stack>
      </Stack>

      {/* Supplier selector */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 120 }}>Viewing price book for:</Typography>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <Select value={supId} onChange={e => setSelectedSupplier(e.target.value)}>
              {suppliers.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={s.code} size="small"
                      sx={{ backgroundColor: BRAND, color: '#fff', fontWeight: 700, fontSize: '0.65rem' }} />
                    <span>{s.name}</span>
                    <Typography variant="caption" color="text.secondary">({s.terms})</Typography>
                    <Chip label={`${supplierMarkups[s.id] ?? s.defaultMarkup}%`} size="small"
                      sx={{ fontSize: '0.6rem', height: 17, fontWeight: 700, backgroundColor: '#fff3e0', color: FI_ORANGE }} />
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem />
          <Stack direction="row" spacing={2}>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight={800} sx={{ color: BRAND }}>{matchedProducts.length}</Typography>
              <Typography variant="caption" color="text.secondary">Products priced</Typography>
            </Box>
            {matchedProducts.length > 0 && (
              <Box textAlign="center">
                <Typography variant="h6" fontWeight={800} sx={{ color: FI_ORANGE }}>
                  ${minPrice.toFixed(2)}–${maxPrice.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Price range</Typography>
              </Box>
            )}
          </Stack>
          <Box flex={1} />
          <TextField
            label={`${supplier.code} Markup %`}
            type="number"
            size="small"
            value={defaultMarkup}
            onChange={(e) => setDefaultMarkup(Number(e.target.value))}
            sx={{ width: 140, '& .MuiOutlinedInput-root': { backgroundColor: '#f5f0ff' } }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            sx={{ backgroundColor: FI_BLUE, textTransform: 'none' }}>
            Add supplier
          </Button>
        </Stack>
      </Paper>

      {/* ── Multi-supplier comparison (cross-supplier, v1.38) ───────────────── */}
      {comparisonGroups.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, border: `1px solid ${FI_BLUE}40` }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1.2}>
            <CompareArrowsIcon sx={{ fontSize: 18, color: FI_BLUE }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: FI_BLUE }}>
              Multi-supplier comparison
            </Typography>
            <Chip label={`${comparisonGroups.length} item${comparisonGroups.length !== 1 ? 's' : ''} sourced from 2+ suppliers`} size="small"
              sx={{ fontSize: '0.64rem', height: 18, fontWeight: 700, backgroundColor: '#eaf0fd', color: FI_BLUE }} />
          </Stack>
          <Stack spacing={1.2}>
            {comparisonGroups.map(g => {
              const cheapest = g.rows[0];
              const dearest = g.rows[g.rows.length - 1];
              const saving = dearest.unitPrice - cheapest.unitPrice;
              const savingPct = dearest.unitPrice > 0 ? (saving / dearest.unitPrice) * 100 : 0;
              return (
                <Box key={g.code} sx={{ border: '1px solid #eee', borderRadius: 1.5, p: 1.2 }}>
                  <Stack direction="row" spacing={1} alignItems="baseline" mb={0.8} flexWrap="wrap" useFlexGap>
                    <Chip label={g.code} size="small"
                      sx={{ fontSize: '0.66rem', fontFamily: 'monospace', fontWeight: 700, backgroundColor: '#f5f0ff', color: BRAND }} />
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                      {g.product?.description ?? cheapest.description}
                    </Typography>
                    {saving > 0.005 && (
                      <Chip label={`Save $${saving.toFixed(2)} (${savingPct.toFixed(0)}%) by buying smart`} size="small"
                        sx={{ fontSize: '0.62rem', height: 18, fontWeight: 700, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {g.rows.map((e, idx) => {
                      const sup = suppliers.find(s => s.id === e.supplierId);
                      const best = idx === 0;
                      return (
                        <Box key={e.id} sx={{
                          display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.6, borderRadius: 1,
                          border: best ? '1px solid #66bb6a' : '1px solid #e0e0e0',
                          backgroundColor: best ? '#e8f5e9' : '#fafafa',
                        }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: best ? '#2e7d32' : '#555' }}>
                            {sup?.code ?? '—'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.62rem', color: '#888' }}>{sup?.name ?? ''}</Typography>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: best ? '#2e7d32' : '#333' }}>
                            ${e.unitPrice.toFixed(2)}
                          </Typography>
                          <Chip label={`${e.supplierCode}`} size="small"
                            sx={{ fontSize: '0.56rem', height: 15, fontFamily: 'monospace', backgroundColor: '#fff', border: '1px solid #ddd', color: '#999' }} />
                          {best && (
                            <Chip label="Best price" size="small"
                              sx={{ fontSize: '0.56rem', height: 15, fontWeight: 700, backgroundColor: '#2e7d32', color: '#fff' }} />
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* ── Supplier codes (created from quotes & bills) — v1.18 ───────────── */}
      <Paper sx={{ p: 2, mb: 3, border: `1px solid ${BRAND}30` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalOfferIcon sx={{ fontSize: 18, color: BRAND }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: BRAND }}>
              Supplier codes — {supplier.name}
            </Typography>
            <Chip label={`${supplierCodes.length}`} size="small"
              sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700, backgroundColor: '#f5f0ff', color: BRAND }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Created when you import a supplier quote or reconcile a bill
          </Typography>
        </Stack>
        {supplierCodes.length === 0 ? (
          <Alert severity="info" sx={{ fontSize: '0.78rem', py: 0.5 }}>
            No supplier codes captured for <strong>{supplier.name}</strong> yet. Import a supplier
            quote on a PO (with "Add to price book" on) and they'll appear here.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                {['Supplier Code', 'Description', 'Cost', 'Inventory Code', 'Type', 'Source'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: BRAND }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {supplierCodes.map(e => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 700, color: BRAND }}>
                    {e.supplierCode}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>{e.description}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>${e.unitPrice.toFixed(2)}</TableCell>
                  <TableCell>
                    {e.internalCode ? (
                      <Chip label={e.internalCode} size="small"
                        sx={{ fontSize: '0.66rem', fontFamily: 'monospace', fontWeight: 700, backgroundColor: '#fff3e0', color: FI_ORANGE }} />
                    ) : (
                      <Chip label="Unlinked" size="small"
                        sx={{ fontSize: '0.62rem', fontWeight: 700, backgroundColor: '#ffebee', color: '#c62828' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={e.stocked ? 'Stocked' : 'Non-stocked'} size="small"
                      sx={{ fontSize: '0.62rem', fontWeight: 700,
                        backgroundColor: e.stocked ? '#e8f5e9' : '#f1f1f4',
                        color: e.stocked ? '#2e7d32' : '#666' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={e.source} size="small" sx={{ fontSize: '0.6rem', height: 17, textTransform: 'capitalize' }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Import preview */}
      {importRows.length > 0 && (() => {
        const rrpFallbackCount = importRows.filter(r => r.rrpMissing).length;
        return (
        <Paper sx={{ p: 2, mb: 3, border: `2px solid ${FI_ORANGE}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" fontWeight={700}>
                Import Preview &mdash; {importRows.length} rows from CSV
              </Typography>
              <Tooltip title="Pricing strategy applied to this import. Click 'Change strategy' to switch before confirming." arrow>
                <Chip label={strategyChip} size="small"
                  onClick={() => setStrategyOpen(true)}
                  sx={{ fontSize: '0.66rem', fontWeight: 700, cursor: 'pointer',
                    backgroundColor: '#fff3e0', color: FI_ORANGE, border: `1px solid ${FI_ORANGE}40` }} />
              </Tooltip>
              <Button size="small" onClick={() => setStrategyOpen(true)}
                sx={{ textTransform: 'none', fontSize: '0.7rem', color: BRAND, p: 0, minWidth: 0 }}>
                Change strategy
              </Button>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Chip label={`${importRows.filter(r => r.resolution === 'matched').length} matched`} size="small" color="success" />
              <Chip label={`${importRows.filter(r => r.resolution === 'suggested').length} suggested`} size="small" color="info" />
              <Chip label={`${importRows.filter(r => r.resolution === 'new').length} new`} size="small" color="warning" />
            </Stack>
          </Stack>

          {/* Bulk resolution actions */}
          <Stack direction="row" spacing={1} mb={1.2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>Resolve:</Typography>
            <Button size="small" variant="outlined" startIcon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
              onClick={bulkConfirmSuggested}
              disabled={!importRows.some(r => r.resolution === 'suggested' && r.action !== 'link')}
              sx={{ textTransform: 'none', fontSize: '0.7rem', borderColor: FI_BLUE, color: FI_BLUE }}>
              Confirm all suggested
            </Button>
            <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon sx={{ fontSize: 15 }} />}
              onClick={bulkCreateNew}
              disabled={!importRows.some(r => r.resolution === 'new' && r.action !== 'create')}
              sx={{ textTransform: 'none', fontSize: '0.7rem', borderColor: FI_ORANGE, color: FI_ORANGE }}>
              Create new for all unmatched (mints FIE- codes)
            </Button>
          </Stack>

          {/*
            ── RRP-missing notice (v1.14.1) ───────────────────────────────────
            If the user picked "Use supplier RRP" but some rows didn't carry an
            RRP value, surface the fall-back behaviour explicitly so they
            aren't surprised by mixed pricing in the imported book.
          */}
          {strategy === 'supplier-rrp' && rrpFallbackCount > 0 && (
            <Alert severity="warning" sx={{ mb: 1.2, fontSize: '0.75rem', py: 0.4 }}>
              {rrpFallbackCount} row{rrpFallbackCount !== 1 ? 's' : ''} had no RRP in the CSV.
              Those rows fall back to the default {defaultMarkup}% margin so the import stays complete.
            </Alert>
          )}

          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fff9f0' }}>
                {['Supplier code', 'Supplier description', 'Cost', 'RRP', 'Sales', 'Inventory item', 'Status', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {importRows.map((r, i) => {
                const willCreate = r.action === 'create' || (r.action === 'link' && !r.linkCode);
                const linked = r.action === 'link' && r.linkCode
                  ? products.find(p => p.code === r.linkCode) ?? null
                  : null;
                const statusChip =
                  r.action === 'skip'
                    ? <Chip label="Skip" size="small" sx={{ fontSize: '0.62rem', backgroundColor: '#f1f1f4', color: '#888' }} />
                    : r.resolution === 'matched'
                      ? <Chip icon={<CheckCircleIcon />} label="Matched" size="small" color="success" sx={{ fontSize: '0.62rem' }} />
                      : r.resolution === 'suggested'
                        ? <Chip label="Suggested" size="small" color="info" sx={{ fontSize: '0.62rem' }} />
                        : <Chip icon={<WarningAmberIcon />} label="New" size="small" color="warning" sx={{ fontSize: '0.62rem' }} />;
                return (
                  <TableRow key={i} sx={{ opacity: r.action === 'skip' ? 0.5 : 1 }}>
                    <TableCell sx={{ fontSize: '0.76rem', fontFamily: 'monospace', fontWeight: 700, color: BRAND }}>{r.code}</TableCell>
                    <TableCell sx={{ fontSize: '0.76rem' }}>{r.description}</TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', fontWeight: 600 }}>${r.price.toFixed(2)}</TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', color: r.rrp !== undefined ? '#333' : '#bbb' }}>
                      {r.rrp !== undefined ? `$${r.rrp.toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#2e7d32' }}>${r.salesPrice.toFixed(2)}</TableCell>
                    <TableCell sx={{ minWidth: 240 }}>
                      {r.action === 'skip' ? (
                        <Typography sx={{ fontSize: '0.72rem', color: '#aaa' }}>—</Typography>
                      ) : (
                        <Stack spacing={0.3}>
                          <Autocomplete
                            size="small"
                            options={products}
                            value={linked}
                            onChange={(_, v) => setRowLink(i, v?.code)}
                            getOptionLabel={p => `${p.code} — ${p.description}`}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={params => (
                              <TextField {...params} placeholder="Link to internal item — or leave blank to create" />
                            )}
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.74rem' } }}
                          />
                          {willCreate && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <AutoFixHighIcon sx={{ fontSize: 12, color: FI_ORANGE }} />
                              <Typography sx={{ fontSize: '0.62rem', color: FI_ORANGE, fontWeight: 700 }}>
                                New supplier-agnostic item will be minted (company prefix code)
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>{statusChip}</TableCell>
                    <TableCell>
                      <Tooltip title={r.action === 'skip' ? 'Include this row' : 'Skip this row'} arrow>
                        <IconButton size="small" onClick={() => setRowAction(i, r.action === 'skip' ? (r.linkCode ? 'link' : 'create') : 'skip')}>
                          <BlockIcon sx={{ fontSize: 16, color: r.action === 'skip' ? '#888' : '#c62828' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {(() => {
            const linkN = importRows.filter(r => r.valid && r.action === 'link' && r.linkCode).length;
            const createN = importRows.filter(r => r.valid && (r.action === 'create' || (r.action === 'link' && !r.linkCode))).length;
            const total = linkN + createN;
            return (
              <Stack direction="row" spacing={1} mt={1.5} alignItems="center" justifyContent="flex-end">
                <Typography sx={{ fontSize: '0.7rem', color: '#888', mr: 'auto' }}>
                  {linkN} linked to existing · {createN} new item{createN !== 1 ? 's' : ''} minted
                </Typography>
                <Button size="small" onClick={() => setImportRows([])} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" size="small" onClick={handleConfirmImport} disabled={total === 0}
                  sx={{ backgroundColor: FI_ORANGE, textTransform: 'none', '&:hover': { backgroundColor: '#e65100' } }}>
                  Confirm import ({total} item{total !== 1 ? 's' : ''})
                </Button>
              </Stack>
            );
          })()}
        </Paper>
        );
      })()}

      {importDone && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImportDone(false)}>
          Price book imported successfully for {supplier.name}.
        </Alert>
      )}

      {/* Search + count */}
      <Stack direction="row" spacing={1} mb={1.5} alignItems="center">
        <TextField placeholder="Search products..." size="small" value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 260 }} />
        <Typography variant="caption" color="text.secondary">
          {filtered.length} of {products.length} products have a price for {supplier.name}
        </Typography>
      </Stack>

      {matchedProducts.length === 0 ? (
        <Alert severity="info">
          No products are priced for <strong>{supplier.name}</strong> yet.
          Import a CSV price list to add pricing, or select a different supplier.
        </Alert>
      ) : (
        <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                {['Item Code', 'Description', 'Category', `${supplier.name} Price`, 'Markup %', 'Sales Price', 'vs Primary', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: BRAND }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(p => {
                const price = priceBook[p.code] ?? 0;
                const primaryBook = PRICE_BOOKS[p.primarySupplier] ?? {};
                const primaryPrice = primaryBook[p.code] ?? p.purchasePrice;
                const diff = price - primaryPrice;
                const salesP = price * (1 + defaultMarkup / 100);
                return (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: FI_ORANGE, fontWeight: 700, fontFamily: 'monospace' }}>
                        {p.code}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{p.description}</TableCell>
                    <TableCell><Chip label={p.category || '—'} size="small" sx={{ fontSize: '0.65rem' }} /></TableCell>
                    <TableCell>
                      <TextField defaultValue={price.toFixed(2)} size="small" variant="outlined"
                        sx={{ width: 85, '& input': { fontSize: '0.78rem', py: 0.5, textAlign: 'right' } }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{defaultMarkup}%</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#2e7d32' }}>${salesP.toFixed(2)}</TableCell>
                    <TableCell>
                      {supId === p.primarySupplier || Math.abs(diff) < 0.01
                        ? <Chip label="Primary" size="small" sx={{ fontSize: '0.65rem', backgroundColor: '#f5f5f5' }} />
                        : diff < 0
                          ? <Chip label={`$${Math.abs(diff).toFixed(2)} cheaper`} size="small" color="success" sx={{ fontSize: '0.65rem' }} />
                          : <Chip label={`$${diff.toFixed(2)} dearer`} size="small" color="warning" sx={{ fontSize: '0.65rem' }} />
                      }
                    </TableCell>
                    <TableCell>
                      {supId !== p.primarySupplier && (
                        <Button size="small" sx={{ textTransform: 'none', fontSize: '0.7rem', color: BRAND, p: 0 }}>
                          Set as primary
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/*
        ── Pricing strategy chooser (v1.14.1) ─────────────────────────────────
        Opens between "Import price book CSV" and the file picker so the user
        consciously decides how supplier costs become customer-facing sales
        prices for THIS import. Card-radio layout keeps the three modes
        readable without burying anything behind a dropdown.
      */}
      <Dialog open={strategyOpen} onClose={() => setStrategyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND, fontSize: '1rem',
          display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileUploadIcon sx={{ color: FI_ORANGE }} />
          Pricing strategy for this import
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#555', mb: 1.5 }}>
            How should the sales price be calculated for each imported row?
            You can change this later from the preview before confirming.
          </Typography>

          <Stack spacing={1}>
            {strategyOptions.map(opt => {
              const selected = strategy === opt.id;
              return (
                <Paper key={opt.id} elevation={0}
                  onClick={() => setStrategy(opt.id)}
                  sx={{ p: 1.2, cursor: 'pointer', borderRadius: 1.5,
                    border: `2px solid ${selected ? FI_ORANGE : '#e0e0e0'}`,
                    backgroundColor: selected ? '#fff8f0' : '#fff',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: FI_ORANGE, backgroundColor: '#fffaf3' } }}>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Radio checked={selected} size="small" sx={{ p: 0, mt: 0.3,
                      color: '#bbb',
                      '&.Mui-checked': { color: FI_ORANGE } }} />
                    <Box sx={{ width: 30, display: 'flex', justifyContent: 'center', mt: 0.3 }}>
                      {opt.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#222' }}>
                        {opt.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#666', mt: 0.2, lineHeight: 1.4 }}>
                        {opt.summary}
                      </Typography>
                      {opt.id === 'custom-margin' && selected && (
                        <TextField
                          autoFocus
                          type="number"
                          size="small"
                          label="Markup %"
                          value={customMargin}
                          onChange={e => setCustomMargin(Number(e.target.value))}
                          onClick={e => e.stopPropagation()}
                          sx={{ mt: 1, width: 140 }}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        />
                      )}
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.74rem', py: 0.4 }}>
            <strong>Preview before commit:</strong> after choosing the CSV, every row's
            cost, RRP and computed sales price are shown side-by-side. Nothing is
            saved until you click <em>Confirm import</em>.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.3 }}>
          <Button onClick={() => setStrategyOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={proceedFromStrategy} startIcon={<FileUploadIcon />}
            disabled={strategy === 'custom-margin' && customMargin <= 0}
            sx={{ backgroundColor: FI_ORANGE, textTransform: 'none',
              '&:hover': { backgroundColor: '#e65100' } }}>
            Choose CSV file
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
